import React from "react";
import ReactDOM from "react-dom";
import { nextId } from "../Form/idGenerator";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable=true]",
].join(",");

function getFocusable(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("aria-hidden") && el.getAttribute("tabindex") !== "-1"
  );
}

export interface ModalFooterProps {
  children?: React.ReactNode;
  className?: string;
}

function ModalFooter({ children, className }: ModalFooterProps) {
  const classes = ["ui-modal__footer", className].filter(Boolean).join(" ");
  return <div className={classes}>{children}</div>;
}

export type ModalSize = "sm" | "md" | "lg";

export interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  title?: React.ReactNode;
  size?: ModalSize;
  dismissOnBackdrop?: boolean;
  className?: string;
  children?: React.ReactNode;
}

interface ModalComponent extends React.FC<ModalProps> {
  Footer: typeof ModalFooter;
}

const Modal: ModalComponent = ({
  isOpen,
  onClose,
  title,
  size = "md",
  dismissOnBackdrop = true,
  className,
  children,
}) => {
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const [titleId] = React.useState(() => nextId("ui-modal-title"));

  React.useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousBodyOverflow = document.body ? document.body.style.overflow : "";
    if (document.body) {
      document.body.style.overflow = "hidden";
    }

    const focusTimer = window.setTimeout(() => {
      const focusables = getFocusable(dialogRef.current);
      const first = focusables[0];
      if (first) {
        first.focus();
      } else if (dialogRef.current) {
        dialogRef.current.focus();
      }
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      if (document.body) {
        document.body.style.overflow = previousBodyOverflow || "";
      }
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen || typeof document === "undefined") {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      if (onClose) {
        onClose();
      }
      return;
    }

    if (event.key === "Tab") {
      const focusables = getFocusable(dialogRef.current);
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) {
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLElement>) => {
    if (dismissOnBackdrop && event.target === event.currentTarget && onClose) {
      onClose();
    }
  };

  const dialogClasses = ["ui-modal__dialog", `ui-modal__dialog--${size}`, className]
    .filter(Boolean)
    .join(" ");

  const node = (
    <div className="ui-modal" role="presentation">
      <button
        type="button"
        className="ui-modal__backdrop"
        aria-label="Fermer"
        onMouseDown={handleBackdropClick}
      />
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={dialogRef}
        className={dialogClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {title ? (
          <div className="ui-modal__header">
            <h2 id={titleId} className="ui-modal__title">
              {title}
            </h2>
          </div>
        ) : null}
        <div className="ui-modal__body">{children}</div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
};

Modal.Footer = ModalFooter;

export default Modal;
